import { tv } from 'tailwind-variants'
import { cn } from '~/utils/cn'
import { ButtonKind, ButtonStandaloneKind } from '../button.types'
import { baseButton } from './base-button.styles'

export const buttonHoverColors = {
  // Regular buttons
  [ButtonKind.primary]: 'bg-button-brand-primary-dark-hover',
  [ButtonKind.secondary]: 'bg-button-subtle-hover',
  [ButtonKind.tertiary]: 'bg-button-brand-primary-light-hover',
  [ButtonKind.neutral]: 'bg-button-brand-primary-light-hover',
  [ButtonKind.critical]: 'bg-button-status-critical-hover',
  [ButtonKind.combobox]: 'bg-neutral-10',

  // Standalone buttons
  standalone_primary: 'bg-button-brand-primary-dark-hover',
  standalone_secondary: 'bg-button-subtle-hover',

  // Default fallback
  default: 'bg-neutral-10',
}

export const getHoverBgColor = (kind: ButtonKind | ButtonStandaloneKind | undefined, isStandalone: boolean): string => {
  // Default to primary button kind if none is provided
  const effectiveKind = kind || (isStandalone ? 'primary' : ButtonKind.primary)

  // Return the appropriate hover background color from our single source of truth
  const key = isStandalone
    ? (`standalone_${effectiveKind}` as keyof typeof buttonHoverColors)
    : (effectiveKind as ButtonKind)

  return buttonHoverColors[key] || buttonHoverColors.default
}

const getHoverClass = (kind: ButtonKind): string => {
  const bgClass = buttonHoverColors[kind]
  return bgClass ? `hover:${bgClass}` : 'hover:bg-neutral-10'
}

export const buttonClassName = tv({
  extend: baseButton,
  base: 'text-center disabled:bg-button-disabled disabled:text-neutral-disabled',
  variants: {
    kind: {
      [ButtonKind.combobox]: cn('bg-neutral-00', 'text-sm text-neutral-tertiary'),
      [ButtonKind.primary]: cn(
        'bg-button-brand-primary-dark-rest',
        getHoverClass(ButtonKind.primary),
        'active:bg-button-brand-primary-dark-pressed',
        'text-neutral-on-brand',
      ),
      [ButtonKind.secondary]: cn(
        'bg-button-subtle-rest',
        getHoverClass(ButtonKind.secondary),
        'active:bg-button-subtle-pressed',
        'border border-solid border-neutral-10',
        'disabled:border-neutral-disabled disabled:bg-button-subtle-rest',
      ),
      [ButtonKind.tertiary]: cn(
        'bg-button-brand-primary-light-rest',
        getHoverClass(ButtonKind.tertiary),
        'active:bg-button-brand-primary-light-pressed',
        'text-brand-primary-rest',
      ),
      [ButtonKind.neutral]: cn(
        'bg-transparent',
        getHoverClass(ButtonKind.neutral),
        'active:bg-button-brand-primary-light-pressed',
        'text-brand-primary-rest',
      ),
      [ButtonKind.critical]: cn(
        'bg-button-status-critical-rest',
        getHoverClass(ButtonKind.critical),
        'active:bg-button-status-critical-pressed',
        'text-neutral-on-brand',
      ),
    },
  },
  defaultVariants: {
    kind: ButtonKind.primary,
  },
})
